/**
 * Friendly French labels for the AI-credit feature/task keys the backend
 * records (the `feature` passed to require_ai_credits / meter_ai_call, which
 * lands on each usage row's `task_type`). Used by the outage modal
 * ({@link CreditGuardHost}) and the per-request usage detail table so the user
 * sees "Génération d'article" instead of "content_pipeline_detail".
 *
 * Also maps each feature to the product SERVICE/area that consumed the credit,
 * so the usage view can group "which service consumed which credit".
 * Unknown keys fall back to a prettified version of the key.
 */

import { tr } from '@/i18n';

export const FEATURE_LABELS: Record<string, string> = {
  crm_recap: tr('shell.features.creditsFeatureLabels.crmSummary'),
  ab_copy: tr('shell.features.creditsFeatureLabels.textABTest'),
  account_intelligence: tr('shell.features.creditsFeatureLabels.accountIntelligence'),
  community_ai: tr('shell.features.creditsFeatureLabels.communityAi'),
  catch_draft: tr('shell.features.creditsFeatureLabels.replyDraftConversations'),
  catch_rewrite: tr('shell.features.creditsFeatureLabels.replyRewriteConversations'),
  community_rules_summary: tr('shell.features.creditsFeatureLabels.communityRulesSummary'),
  content_generation: tr('shell.features.creditsFeatureLabels.contentGeneration'),
  generate_content: tr('shell.features.creditsFeatureLabels.contentGeneration'),
  content_pipeline_theme: tr('shell.features.creditsFeatureLabels.themeGeneration'),
  content_pipeline_title: tr('shell.features.creditsFeatureLabels.titleGeneration'),
  content_pipeline_description: tr('shell.features.creditsFeatureLabels.descriptionGeneration'),
  content_pipeline_detail: tr('shell.features.creditsFeatureLabels.contentWriting'),
  content_studio: tr('shell.features.creditsFeatureLabels.contentStudio'),
  generate_hashtags: tr('shell.features.creditsFeatureLabels.hashtagGeneration'),
  generate_image_prompts: tr('shell.features.creditsFeatureLabels.imagePrompts'),
  ghostwriter_generate: tr('shell.features.creditsFeatureLabels.ghostwriterWriting'),
  ghostwriter_rewrite: tr('shell.features.creditsFeatureLabels.ghostwriterRewriting'),
  ghostwriter_translate: tr('shell.features.creditsFeatureLabels.ghostwriterTranslate'),
  improve_content: tr('shell.features.creditsFeatureLabels.contentImprovement'),
  langgraph_execute: tr('shell.features.creditsFeatureLabels.agentExecution'),
  plan_nlu: tr('shell.features.creditsFeatureLabels.planningUnderstanding'),
  post_review: tr('shell.features.creditsFeatureLabels.publicationReview'),
  post_review_panel: tr('shell.features.creditsFeatureLabels.aiReviewPanel'),
  prospection_ai_search: tr('shell.features.creditsFeatureLabels.aiProspectingResearch'),
  prospection_calendar: tr('shell.features.creditsFeatureLabels.prospectingCalendar'),
  prospection_strategy: tr('shell.features.creditsFeatureLabels.prospectingStrategy'),
  reply_assistant: tr('shell.features.creditsFeatureLabels.replyAssistant'),
  repurpose: tr('shell.features.creditsFeatureLabels.contentRepurposing'),
  staff_chat: tr('shell.features.creditsFeatureLabels.aiAssistantChat'),
  suggestions: tr('shell.features.creditsFeatureLabels.aiSuggestions'),
  test_feature: tr('shell.features.creditsFeatureLabels.test'),
  // Actual prod task_types observed in ai_usage_records:
  copilot: tr('shell.features.creditsFeatureLabels.copilot'),
  persona_task: tr('shell.features.creditsFeatureLabels.agentPersonaTask'),
  extraction: tr('shell.features.creditsFeatureLabels.extractionMemory'),
  rag_embeddings: tr('shell.features.creditsFeatureLabels.ragEmbeddings'),
  analytics_vision: tr('shell.features.creditsFeatureLabels.visualAnalysisAnalytics'),
  studio_generate: tr('shell.features.creditsFeatureLabels.studioGeneration'),
  carousel: tr('shell.features.creditsFeatureLabels.carousel'),
  thread: tr('shell.features.creditsFeatureLabels.thread'),
  generation: tr('shell.features.creditsFeatureLabels.generation'),
  workflow: tr('shell.features.creditsFeatureLabels.workflow'),
  sdr: tr('shell.features.creditsFeatureLabels.sdrAutopilot'),
  // Generic bucket for otherwise-uninstrumented background/agentic LLM calls
  // (debit_for_usage attributes these instead of writing a NULL task_type).
  agent_llm: tr('shell.features.creditsFeatureLabels.aiCallAgent'),
  // RAG / semantic retrieval features.
  semantic_search: tr('shell.features.creditsFeatureLabels.semanticSearch'),
  rag_search_embed: tr('shell.features.creditsFeatureLabels.ragSearchEmbeddings'),
  // Pre-flight-gated orchestration entry points (L-gate).
  mas_run: tr('shell.features.creditsFeatureLabels.aiTeamExecution'),
  mas_plan: tr('shell.features.creditsFeatureLabels.aiTeamPlanning'),
  mas_team_plan: tr('shell.features.creditsFeatureLabels.aiTeamComposition'),
  staff_team_run: tr('shell.features.creditsFeatureLabels.aiTeamMission'),
  crm_conversation: tr('shell.features.creditsFeatureLabels.conversationAnalysis'),
  crm_sdr: tr('shell.features.creditsFeatureLabels.sdrAutopilot'),
  crm_email_draft: tr('crmEmailDrafts.creditFeatureLabel'),
  // Credits-coverage keys (services-python feature_cost.py + per-service *_CREDIT_FEATURE).
  advisor_content: tr('shell.features.creditsFeatureLabels.advisorContent'),
  advisor_ads: tr('shell.features.creditsFeatureLabels.advisorAds'),
  advisor_prospection: tr('shell.features.creditsFeatureLabels.advisorProspection'),
  advisor_campaign: tr('shell.features.creditsFeatureLabels.advisorCampaign'),
  copilot_turn: tr('shell.features.creditsFeatureLabels.copilotTurn'),
  copilot_transcribe: tr('shell.features.creditsFeatureLabels.copilotTranscribe'),
  assistant_do: tr('shell.features.creditsFeatureLabels.assistantDo'),
  launch_plan: tr('shell.features.creditsFeatureLabels.launchPlan'),
  build_in_public: tr('shell.features.creditsFeatureLabels.buildInPublic'),
  memory_extraction: tr('shell.features.creditsFeatureLabels.memoryExtraction'),
  rag_document_upload: tr('shell.features.creditsFeatureLabels.ragDocumentUpload'),
  rag_url_ingest: tr('shell.features.creditsFeatureLabels.ragUrlIngest'),
  content_ideas: tr('shell.features.creditsFeatureLabels.contentIdeas'),
  humanize_content: tr('shell.features.creditsFeatureLabels.humanizeContent'),
  plagiarism_check: tr('shell.features.creditsFeatureLabels.plagiarismCheck'),
  post_review_loop: tr('shell.features.creditsFeatureLabels.postReviewLoop'),
  generate_calendar: tr('shell.features.creditsFeatureLabels.generateCalendar'),
  content_performance_learn: tr('shell.features.creditsFeatureLabels.contentPerformanceLearn'),
  contact_enrich: tr('shell.features.creditsFeatureLabels.contactEnrich'),
  company_enrich: tr('shell.features.creditsFeatureLabels.companyEnrich'),
  prospect_discovery: tr('shell.features.creditsFeatureLabels.prospectDiscovery'),
  prospect_enrich: tr('shell.features.creditsFeatureLabels.prospectEnrich'),
  b2b_contact_enrich: tr('shell.features.creditsFeatureLabels.b2bContactEnrich'),
  b2b_visitor_identify: tr('shell.features.creditsFeatureLabels.b2bVisitorIdentify'),
  b2b_job_change_scan: tr('shell.features.creditsFeatureLabels.b2bJobChangeScan'),
  apollo_contact_enrich: tr('shell.features.creditsFeatureLabels.apolloContactEnrich'),
  apollo_company_enrich: tr('shell.features.creditsFeatureLabels.apolloCompanyEnrich'),
  apollo_prospect_discovery: tr('shell.features.creditsFeatureLabels.apolloProspectDiscovery'),
  apollo_b2b_contact_enrich: tr('shell.features.creditsFeatureLabels.apolloB2bContactEnrich'),
  social_target_discovery: tr('shell.features.creditsFeatureLabels.socialTargetDiscovery'),
  call_insight: tr('shell.features.creditsFeatureLabels.callInsight'),
  crm_journey_advise: tr('shell.features.creditsFeatureLabels.crmJourneyAdvise'),
  crm_scoring_recommend: tr('shell.features.creditsFeatureLabels.crmScoringRecommend'),
  email_organize: tr('shell.features.creditsFeatureLabels.emailOrganize'),
  growth_recommendations: tr('shell.features.creditsFeatureLabels.growthRecommendations'),
  program_invite_email: tr('shell.features.creditsFeatureLabels.programInviteEmail'),
  lifecycle_email: tr('shell.features.creditsFeatureLabels.lifecycleEmail'),
  image_generation: tr('shell.features.creditsFeatureLabels.imageGeneration'),
  video_generation: tr('shell.features.creditsFeatureLabels.videoGeneration'),
  image_to_video: tr('shell.features.creditsFeatureLabels.imageToVideo'),
  avatar_generation: tr('shell.features.creditsFeatureLabels.avatarGeneration'),
  product_brief_extract: tr('shell.features.creditsFeatureLabels.productBriefExtract'),
  product_brief_competitors: tr('shell.features.creditsFeatureLabels.productBriefCompetitors'),
  product_brief_seed: tr('shell.features.creditsFeatureLabels.productBriefSeed'),
  daily_moves_regenerate: tr('shell.features.creditsFeatureLabels.dailyMovesRegenerate'),
  strategy_checkin: tr('shell.features.creditsFeatureLabels.strategyCheckin'),
  launch_kit_generate: tr('shell.features.creditsFeatureLabels.launchKitGenerate'),
  launch_kit_directory_copy: tr('shell.features.creditsFeatureLabels.launchKitDirectoryCopy'),
  autopilot_content_channel: tr('shell.features.creditsFeatureLabels.autopilotContentChannel'),
  autopilot_email_sequence: tr('shell.features.creditsFeatureLabels.autopilotEmailSequence'),
  catch_capture: tr('shell.features.creditsFeatureLabels.catchCapture'),
  creative_ad_run: tr('shell.features.creditsFeatureLabels.creativeAdRun'),
  ghostwriter_ad_copy: tr('shell.features.creditsFeatureLabels.ghostwriterAdCopy'),
  learning_transpose: tr('shell.features.creditsFeatureLabels.learningTranspose'),
  marketing_strategy_draft: tr('shell.features.creditsFeatureLabels.marketingStrategyDraft'),
  source_studio_ingest: tr('shell.features.creditsFeatureLabels.sourceStudioIngest'),
  source_studio_generate: tr('shell.features.creditsFeatureLabels.sourceStudioGenerate'),
  source_studio_chat: tr('shell.features.creditsFeatureLabels.sourceStudioChat'),
  source_studio_audio: tr('shell.features.creditsFeatureLabels.sourceStudioAudio'),
  translate_localize: tr('shell.features.creditsFeatureLabels.translateLocalize'),
  site_audit: tr('shell.features.creditsFeatureLabels.siteAudit'),
  analytics_site_audit: tr('shell.features.creditsFeatureLabels.analyticsSiteAudit'),
  analytics_sentiment_analyze: tr('shell.features.creditsFeatureLabels.analyticsSentimentAnalyze'),
  analytics_trends_detect: tr('shell.features.creditsFeatureLabels.analyticsTrendsDetect'),
  analytics_competitor_compare: tr('shell.features.creditsFeatureLabels.analyticsCompetitorCompare'),
  analytics_crm_ml: tr('shell.features.creditsFeatureLabels.analyticsCrmMl'),
  analytics_subject_summary: tr('shell.features.creditsFeatureLabels.analyticsSubjectSummary'),
  analytics_report_narrative: tr('shell.features.creditsFeatureLabels.analyticsReportNarrative'),
  analytics_geo: tr('shell.features.creditsFeatureLabels.analyticsGeo'),
  content_image_generate: tr('shell.features.creditsFeatureLabels.contentImageGenerate'),
  content_ai_fill_template: tr('shell.features.creditsFeatureLabels.contentAiFillTemplate'),
  content_ai_suggest_palette: tr('shell.features.creditsFeatureLabels.contentAiSuggestPalette'),
  content_video_generate: tr('shell.features.creditsFeatureLabels.contentVideoGenerate'),
  content_video_transcribe: tr('shell.features.creditsFeatureLabels.contentVideoTranscribe'),
  content_video_repurpose: tr('shell.features.creditsFeatureLabels.contentVideoRepurpose'),
  content_audio_voiceover: tr('shell.features.creditsFeatureLabels.contentAudioVoiceover'),
  content_audio_voice_clone: tr('shell.features.creditsFeatureLabels.contentAudioVoiceClone'),
  content_audio_voice_enroll: tr('shell.features.creditsFeatureLabels.contentAudioVoiceEnroll'),
  content_video_avatar: tr('shell.features.creditsFeatureLabels.contentVideoAvatar'),
  content_image_remove_background: tr('shell.features.creditsFeatureLabels.contentImageRemoveBackground'),
  content_image_upscale: tr('shell.features.creditsFeatureLabels.contentImageUpscale'),
  content_image_cleanup: tr('shell.features.creditsFeatureLabels.contentImageCleanup'),
  content_image_inpaint: tr('shell.features.creditsFeatureLabels.contentImageInpaint'),
  content_translate_locale: tr('shell.features.creditsFeatureLabels.contentTranslateLocale'),
  content_carousel_ai_slide: tr('shell.features.creditsFeatureLabels.contentCarouselAiSlide'),
  content_carousel_copy_generate: tr('shell.features.creditsFeatureLabels.contentCarouselCopyGenerate'),
  content_infographic_generate: tr('shell.features.creditsFeatureLabels.contentInfographicGenerate'),
  content_animation_generate: tr('shell.features.creditsFeatureLabels.contentAnimationGenerate'),
  content_post_pack_generate: tr('shell.features.creditsFeatureLabels.contentPostPackGenerate'),
  content_video_script_generate: tr('shell.features.creditsFeatureLabels.contentVideoScriptGenerate'),
  content_longform_outline: tr('shell.features.creditsFeatureLabels.contentLongformOutline'),
  content_longform_generate: tr('shell.features.creditsFeatureLabels.contentLongformGenerate'),
  content_longform_section: tr('shell.features.creditsFeatureLabels.contentLongformSection'),
  content_seo_suggest: tr('shell.features.creditsFeatureLabels.contentSeoSuggest'),
  content_geo_suggest: tr('shell.features.creditsFeatureLabels.contentGeoSuggest'),
};

/** Which product area consumed the credit (for the per-service usage rollup). */
export const FEATURE_SERVICE: Record<string, string> = {
  crm_recap: 'CRM',
  account_intelligence: tr('shell.features.creditsFeatureLabels.prospecting'),
  prospection_ai_search: tr('shell.features.creditsFeatureLabels.prospecting'),
  prospection_calendar: tr('shell.features.creditsFeatureLabels.prospecting'),
  prospection_strategy: tr('shell.features.creditsFeatureLabels.prospecting'),
  ab_copy: tr('shell.features.creditsFeatureLabels.content'),
  content_generation: tr('shell.features.creditsFeatureLabels.content'),
  generate_content: tr('shell.features.creditsFeatureLabels.content'),
  content_pipeline_theme: tr('shell.features.creditsFeatureLabels.content'),
  content_pipeline_title: tr('shell.features.creditsFeatureLabels.content'),
  content_pipeline_description: tr('shell.features.creditsFeatureLabels.content'),
  content_pipeline_detail: tr('shell.features.creditsFeatureLabels.content'),
  content_studio: tr('shell.features.creditsFeatureLabels.studio'),
  generate_hashtags: tr('shell.features.creditsFeatureLabels.content'),
  generate_image_prompts: tr('shell.features.creditsFeatureLabels.studio'),
  ghostwriter_generate: tr('shell.features.creditsFeatureLabels.ghostwriter'),
  ghostwriter_rewrite: tr('shell.features.creditsFeatureLabels.ghostwriter'),
  ghostwriter_translate: tr('shell.features.creditsFeatureLabels.ghostwriter'),
  improve_content: tr('shell.features.creditsFeatureLabels.content'),
  post_review: tr('shell.features.creditsFeatureLabels.content'),
  post_review_panel: tr('shell.features.creditsFeatureLabels.content'),
  repurpose: tr('shell.features.creditsFeatureLabels.content'),
  community_ai: tr('shell.features.creditsFeatureLabels.community'),
  catch_draft: tr('shell.features.creditsFeatureLabels.listening'),
  catch_rewrite: tr('shell.features.creditsFeatureLabels.listening'),
  community_rules_summary: tr('shell.features.creditsFeatureLabels.community'),
  reply_assistant: tr('shell.features.creditsFeatureLabels.inbox'),
  suggestions: tr('shell.features.creditsFeatureLabels.ai'),
  staff_chat: tr('shell.features.creditsFeatureLabels.aiAssistant'),
  langgraph_execute: tr('shell.features.creditsFeatureLabels.aiTeam'),
  plan_nlu: tr('shell.features.creditsFeatureLabels.aiTeam'),
  copilot: tr('shell.features.creditsFeatureLabels.copilot'),
  persona_task: tr('shell.features.creditsFeatureLabels.aiTeam'),
  extraction: tr('shell.features.creditsFeatureLabels.memory'),
  rag_embeddings: 'RAG',
  analytics_vision: tr('shell.features.creditsFeatureLabels.analytics'),
  studio_generate: tr('shell.features.creditsFeatureLabels.studio'),
  carousel: tr('shell.features.creditsFeatureLabels.studio'),
  thread: tr('shell.features.creditsFeatureLabels.community'),
  generation: tr('shell.features.creditsFeatureLabels.content'),
  workflow: tr('shell.features.creditsFeatureLabels.workflows'),
  sdr: tr('shell.features.creditsFeatureLabels.prospecting'),
  agent_llm: tr('shell.features.creditsFeatureLabels.aiAgents'),
  semantic_search: 'RAG',
  rag_search_embed: 'RAG',
  mas_run: tr('shell.features.creditsFeatureLabels.aiTeam'),
  mas_plan: tr('shell.features.creditsFeatureLabels.aiTeam'),
  mas_team_plan: tr('shell.features.creditsFeatureLabels.aiTeam'),
  staff_team_run: tr('shell.features.creditsFeatureLabels.aiTeam'),
  crm_conversation: 'CRM',
  crm_email_draft: 'CRM',
  crm_sdr: tr('shell.features.creditsFeatureLabels.prospecting'),
  advisor_content: tr('shell.features.creditsFeatureLabels.ai'),
  advisor_ads: tr('shell.features.creditsFeatureLabels.ai'),
  advisor_prospection: tr('shell.features.creditsFeatureLabels.prospecting'),
  advisor_campaign: tr('shell.features.creditsFeatureLabels.ai'),
  copilot_turn: tr('shell.features.creditsFeatureLabels.copilot'),
  copilot_transcribe: tr('shell.features.creditsFeatureLabels.copilot'),
  assistant_do: tr('shell.features.creditsFeatureLabels.aiAssistant'),
  launch_plan: tr('shell.features.creditsFeatureLabels.ai'),
  build_in_public: tr('shell.features.creditsFeatureLabels.content'),
  memory_extraction: tr('shell.features.creditsFeatureLabels.memory'),
  rag_document_upload: 'RAG',
  rag_url_ingest: 'RAG',
  content_ideas: tr('shell.features.creditsFeatureLabels.content'),
  humanize_content: tr('shell.features.creditsFeatureLabels.content'),
  plagiarism_check: tr('shell.features.creditsFeatureLabels.content'),
  post_review_loop: tr('shell.features.creditsFeatureLabels.content'),
  generate_calendar: tr('shell.features.creditsFeatureLabels.content'),
  content_performance_learn: tr('shell.features.creditsFeatureLabels.content'),
  contact_enrich: 'CRM',
  company_enrich: 'CRM',
  prospect_discovery: tr('shell.features.creditsFeatureLabels.prospecting'),
  prospect_enrich: tr('shell.features.creditsFeatureLabels.prospecting'),
  b2b_contact_enrich: tr('shell.features.creditsFeatureLabels.prospecting'),
  b2b_visitor_identify: tr('shell.features.creditsFeatureLabels.prospecting'),
  b2b_job_change_scan: tr('shell.features.creditsFeatureLabels.prospecting'),
  apollo_contact_enrich: tr('shell.features.creditsFeatureLabels.prospecting'),
  apollo_company_enrich: tr('shell.features.creditsFeatureLabels.prospecting'),
  apollo_prospect_discovery: tr('shell.features.creditsFeatureLabels.prospecting'),
  apollo_b2b_contact_enrich: tr('shell.features.creditsFeatureLabels.prospecting'),
  social_target_discovery: tr('shell.features.creditsFeatureLabels.prospecting'),
  call_insight: 'CRM',
  crm_journey_advise: 'CRM',
  crm_scoring_recommend: 'CRM',
  email_organize: tr('shell.features.creditsFeatureLabels.inbox'),
  growth_recommendations: tr('shell.features.creditsFeatureLabels.ai'),
  program_invite_email: tr('shell.features.creditsFeatureLabels.ai'),
  lifecycle_email: tr('shell.features.creditsFeatureLabels.ai'),
  image_generation: tr('shell.features.creditsFeatureLabels.studio'),
  video_generation: tr('shell.features.creditsFeatureLabels.studio'),
  image_to_video: tr('shell.features.creditsFeatureLabels.studio'),
  avatar_generation: tr('shell.features.creditsFeatureLabels.studio'),
  product_brief_extract: tr('shell.features.creditsFeatureLabels.ai'),
  product_brief_competitors: tr('shell.features.creditsFeatureLabels.ai'),
  product_brief_seed: tr('shell.features.creditsFeatureLabels.ai'),
  daily_moves_regenerate: tr('shell.features.creditsFeatureLabels.ai'),
  strategy_checkin: tr('shell.features.creditsFeatureLabels.ai'),
  launch_kit_generate: tr('shell.features.creditsFeatureLabels.ai'),
  launch_kit_directory_copy: tr('shell.features.creditsFeatureLabels.ai'),
  autopilot_content_channel: tr('shell.features.creditsFeatureLabels.ai'),
  autopilot_email_sequence: tr('shell.features.creditsFeatureLabels.ai'),
  catch_capture: tr('shell.features.creditsFeatureLabels.listening'),
  creative_ad_run: tr('shell.features.creditsFeatureLabels.ai'),
  ghostwriter_ad_copy: tr('shell.features.creditsFeatureLabels.ghostwriter'),
  learning_transpose: tr('shell.features.creditsFeatureLabels.content'),
  marketing_strategy_draft: tr('shell.features.creditsFeatureLabels.ai'),
  source_studio_ingest: tr('shell.features.creditsFeatureLabels.studio'),
  source_studio_generate: tr('shell.features.creditsFeatureLabels.studio'),
  source_studio_chat: tr('shell.features.creditsFeatureLabels.studio'),
  source_studio_audio: tr('shell.features.creditsFeatureLabels.studio'),
  translate_localize: tr('shell.features.creditsFeatureLabels.content'),
  site_audit: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_site_audit: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_sentiment_analyze: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_trends_detect: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_competitor_compare: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_crm_ml: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_subject_summary: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_report_narrative: tr('shell.features.creditsFeatureLabels.analytics'),
  analytics_geo: tr('shell.features.creditsFeatureLabels.analytics'),
  content_image_generate: tr('shell.features.creditsFeatureLabels.studio'),
  content_ai_fill_template: tr('shell.features.creditsFeatureLabels.studio'),
  content_ai_suggest_palette: tr('shell.features.creditsFeatureLabels.studio'),
  content_video_generate: tr('shell.features.creditsFeatureLabels.studio'),
  content_video_transcribe: tr('shell.features.creditsFeatureLabels.studio'),
  content_video_repurpose: tr('shell.features.creditsFeatureLabels.studio'),
  content_audio_voiceover: tr('shell.features.creditsFeatureLabels.studio'),
  content_audio_voice_clone: tr('shell.features.creditsFeatureLabels.studio'),
  content_audio_voice_enroll: tr('shell.features.creditsFeatureLabels.studio'),
  content_video_avatar: tr('shell.features.creditsFeatureLabels.studio'),
  content_image_remove_background: tr('shell.features.creditsFeatureLabels.studio'),
  content_image_upscale: tr('shell.features.creditsFeatureLabels.studio'),
  content_image_cleanup: tr('shell.features.creditsFeatureLabels.studio'),
  content_image_inpaint: tr('shell.features.creditsFeatureLabels.studio'),
  content_translate_locale: tr('shell.features.creditsFeatureLabels.content'),
  content_carousel_ai_slide: tr('shell.features.creditsFeatureLabels.studio'),
  content_carousel_copy_generate: tr('shell.features.creditsFeatureLabels.studio'),
  content_infographic_generate: tr('shell.features.creditsFeatureLabels.studio'),
  content_animation_generate: tr('shell.features.creditsFeatureLabels.studio'),
  content_post_pack_generate: tr('shell.features.creditsFeatureLabels.content'),
  content_video_script_generate: tr('shell.features.creditsFeatureLabels.studio'),
  content_longform_outline: tr('shell.features.creditsFeatureLabels.content'),
  content_longform_generate: tr('shell.features.creditsFeatureLabels.content'),
  content_longform_section: tr('shell.features.creditsFeatureLabels.content'),
  content_seo_suggest: tr('shell.features.creditsFeatureLabels.content'),
  content_geo_suggest: tr('shell.features.creditsFeatureLabels.content'),
};

/** Prettify an unknown key: `content_pipeline_detail` → `Content pipeline detail`. */
function prettify(key: string): string {
  const s = key.replace(/[_.-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}

export function featureLabel(feature: string | null | undefined): string {
  if (!feature) return '—';
  return FEATURE_LABELS[feature] ?? prettify(feature);
}

export function featureService(feature: string | null | undefined): string {
  if (!feature) return '—';
  return FEATURE_SERVICE[feature] ?? tr('shell.features.creditsFeatureLabels.ai');
}
